import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Clock, TrendingUp, Download, Filter, ChevronDown, ChevronUp } from 'lucide-react';
import { complianceService } from '../../../services/complianceService';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const ComplianceDashboard = ({ organizationId }) => {
  const [activeTab, setActiveTab] = useState('policies');
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [inactiveMembers, setInactiveMembers] = useState({ thirtyDays: [], sixtyDays: [], ninetyDays: [] });
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPagination, setAuditPagination] = useState(null);
  const [selectedThreshold, setSelectedThreshold] = useState(30);
  const [auditFilters, setAuditFilters] = useState({ changeType: '', page: 1 });
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    if (organizationId) {
      loadComplianceData();
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId && activeTab === 'audit') {
      loadAuditLogs();
    }
  }, [auditFilters, organizationId, activeTab]);

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const [statsResult, policiesResult, inactiveResult] = await Promise?.all([
        complianceService?.getComplianceStatistics(organizationId),
        complianceService?.getPolicies(organizationId),
        complianceService?.getInactiveMembersByThresholds(organizationId)
      ]);

      if (statsResult?.error) throw statsResult?.error;
      if (policiesResult?.error) throw policiesResult?.error;
      if (inactiveResult?.error) throw inactiveResult?.error;

      setStatistics(statsResult?.data);
      setPolicies(policiesResult?.data || []);
      setInactiveMembers(inactiveResult?.data || { thirtyDays: [], sixtyDays: [], ninetyDays: [] });
    } catch (error) {
      console.error('Error loading compliance data:', error);
      toast?.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const { data, error, pagination } = await complianceService?.getPermissionAuditLog(
        organizationId,
        auditFilters
      );

      if (error) throw error;

      setAuditLogs(data || []);
      setAuditPagination(pagination);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast?.error('Failed to load audit logs');
    }
  };

  const handleExportAuditLog = async () => {
    try {
      const { data, error } = await complianceService?.exportAuditLog(organizationId, auditFilters);
      if (error) throw error;

      complianceService?.downloadCSV(data, `permission-audit-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
      toast?.success('Audit log exported successfully');
    } catch (error) {
      console.error('Error exporting audit log:', error);
      toast?.error('Failed to export audit log');
    }
  };

  const getInactiveMembersByThreshold = () => {
    switch (selectedThreshold) {
      case 30:
        return inactiveMembers?.thirtyDays || [];
      case 60:
        return inactiveMembers?.sixtyDays || [];
      case 90:
        return inactiveMembers?.ninetyDays || [];
      default:
        return [];
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'escalation':
        return 'text-red-600 bg-red-50';
      case 'role_change':
        return 'text-blue-600 bg-blue-50';
      case 'permission_grant':
        return 'text-green-600 bg-green-50';
      case 'permission_revoke':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getPolicyStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'inactive':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Policies</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{statistics?.activePolicies || 0}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Inactive Members</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{statistics?.totalInactiveMembers || 0}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Audit Logs</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{statistics?.totalAuditLogs || 0}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('policies')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'policies' ?'border-purple-600 text-purple-600' :'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Password Policies
            </button>
            <button
              onClick={() => setActiveTab('inactive')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'inactive' ?'border-purple-600 text-purple-600' :'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Inactive Members
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'audit' ?'border-purple-600 text-purple-600' :'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Permission Audit Trail
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Password Policies Tab */}
          {activeTab === 'policies' && (
            <div className="space-y-4">
              {policies?.length === 0 ? (
                <div className="text-center py-12">
                  <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No compliance policies configured</p>
                </div>
              ) : (
                policies?.map((policy) => (
                  <div key={policy?.id} className="border border-gray-200 rounded-lg">
                    <div
                      className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedPolicy(expandedPolicy === policy?.id ? null : policy?.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-purple-600" />
                        <div>
                          <h3 className="font-medium text-gray-900">{policy?.policy_name}</h3>
                          <p className="text-sm text-gray-600 capitalize">{policy?.policy_type} Policy</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPolicyStatusColor(policy?.enforcement_status)}`}>
                          {policy?.enforcement_status?.toUpperCase()}
                        </span>
                        {expandedPolicy === policy?.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {expandedPolicy === policy?.id && (
                      <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                        <div className="mt-4 space-y-2">
                          {policy?.policy_type === 'password' && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Minimum Length:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.min_length || 'N/A'} characters</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Complexity Requirements:</span>
                                <span className="font-medium text-gray-900">
                                  {policy?.policy_config?.require_uppercase ? 'Uppercase, ' : ''}
                                  {policy?.policy_config?.require_lowercase ? 'Lowercase, ' : ''}
                                  {policy?.policy_config?.require_numbers ? 'Numbers, ' : ''}
                                  {policy?.policy_config?.require_special_chars ? 'Special Chars' : ''}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Expiration:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.expiration_days || 'Never'} days</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Prevent Reuse:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.prevent_reuse || 0} previous passwords</span>
                              </div>
                            </>
                          )}
                          {policy?.policy_type === 'inactivity' && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">30-Day Threshold:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.threshold_30_days ? 'Enabled' : 'Disabled'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">60-Day Threshold:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.threshold_60_days ? 'Enabled' : 'Disabled'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">90-Day Threshold:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.threshold_90_days ? 'Enabled' : 'Disabled'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Auto Suspend:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.auto_suspend ? 'Yes' : 'No'}</span>
                              </div>
                            </>
                          )}
                          {policy?.policy_type === 'access' && (
                            <>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Require MFA:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.require_mfa ? 'Yes' : 'No'}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Session Timeout:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.session_timeout_minutes || 60} minutes</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Approval for Escalation:</span>
                                <span className="font-medium text-gray-900">{policy?.policy_config?.require_approval_for_escalation ? 'Required' : 'Not Required'}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Inactive Members Tab */}
          {activeTab === 'inactive' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Threshold:</span>
                  <select
                    value={selectedThreshold}
                    onChange={(e) => setSelectedThreshold(parseInt(e?.target?.value))}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={30}>30 Days</option>
                    <option value={60}>60 Days</option>
                    <option value={90}>90 Days</option>
                  </select>
                </div>
                <div className="text-sm text-gray-600">
                  {getInactiveMembersByThreshold()?.length} inactive members
                </div>
              </div>

              {getInactiveMembersByThreshold()?.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No inactive members found for this threshold</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Inactive</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getInactiveMembersByThreshold()?.map((member) => (
                        <tr key={member?.member_id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-gray-900">{member?.user_name || 'Unknown'}</p>
                              <p className="text-sm text-gray-600">{member?.user_email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {member?.role?.toUpperCase()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(member?.last_active_at)?.toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              member?.days_inactive >= 90 ? 'bg-red-100 text-red-800' :
                              member?.days_inactive >= 60 ? 'bg-orange-100 text-orange-800': 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {member?.days_inactive} days
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Permission Audit Trail Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">Change Type:</span>
                  <select
                    value={auditFilters?.changeType}
                    onChange={(e) => setAuditFilters({ ...auditFilters, changeType: e?.target?.value, page: 1 })}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Changes</option>
                    <option value="role_change">Role Changes</option>
                    <option value="permission_grant">Permission Grants</option>
                    <option value="permission_revoke">Permission Revokes</option>
                    <option value="escalation">Escalations</option>
                  </select>
                </div>
                <Button
                  onClick={handleExportAuditLog}
                  variant="outline"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>

              {auditLogs?.length === 0 ? (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No audit logs found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs?.map((log) => (
                    <div key={log?.id} className="border border-gray-200 rounded-lg">
                      <div
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getChangeTypeColor(log?.change_type)}`}>
                            {log?.change_type?.replace('_', ' ')?.toUpperCase()}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {log?.team_member?.user?.full_name || log?.team_member?.user?.email || 'Unknown Member'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {log?.previous_role} → {log?.new_role}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-600">
                              {new Date(log?.created_at)?.toLocaleDateString()}
                            </p>
                            <p className="text-xs text-gray-500">
                              by {log?.changed_by_user?.full_name || log?.changed_by_user?.email || 'System'}
                            </p>
                          </div>
                        </div>
                        {expandedLog === log?.id ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 ml-2" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 ml-2" />
                        )}
                      </div>

                      {expandedLog === log?.id && (
                        <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                          <div className="mt-4 space-y-2 text-sm">
                            {log?.change_reason && (
                              <div>
                                <span className="text-gray-600">Reason: </span>
                                <span className="text-gray-900">{log?.change_reason}</span>
                              </div>
                            )}
                            {log?.ip_address && (
                              <div>
                                <span className="text-gray-600">IP Address: </span>
                                <span className="text-gray-900">{log?.ip_address}</span>
                              </div>
                            )}
                            {log?.metadata?.is_escalation && (
                              <div className="flex items-center gap-2 text-red-600">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="font-medium">Role Escalation Detected</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Pagination */}
                  {auditPagination && auditPagination?.totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-sm text-gray-600">
                        Showing {((auditPagination?.page - 1) * auditPagination?.pageSize) + 1} to{' '}
                        {Math?.min(auditPagination?.page * auditPagination?.pageSize, auditPagination?.total)} of{' '}
                        {auditPagination?.total} results
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setAuditFilters({ ...auditFilters, page: auditFilters?.page - 1 })}
                          disabled={auditFilters?.page === 1}
                          variant="outline"
                          size="sm"
                        >
                          Previous
                        </Button>
                        <Button
                          onClick={() => setAuditFilters({ ...auditFilters, page: auditFilters?.page + 1 })}
                          disabled={auditFilters?.page >= auditPagination?.totalPages}
                          variant="outline"
                          size="sm"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComplianceDashboard;